import { Box, Typography } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import { axisClasses } from "@mui/x-charts/ChartsAxis";
import { formatCurrency, theme } from "../../../utils";
import { KPIReportResponse, LeadMetric } from "../../../types";

export const KPIChart = ({
  data,
}: {
  data?: KPIReportResponse;
  loading?: boolean;
}) => {
  const chartData =
    data?.leadMetrics?.map((item: LeadMetric) => ({
      tech: item.leadName,
      washes: item.washesCount,
      revenue: item.revenue,
    })) || [];
  const maxWashes = Math.max(...chartData.map((item) => item.washes || 0));

  const maxRevenue = Math.max(...chartData.map((item) => item.revenue || 0));

  const washesInterval = maxWashes > 1000 ? 1000 : 20;

  const revenueInterval = maxRevenue > 1000000 ? 5000000 : 10000;

  const washesAxisMax = Math.ceil(maxWashes / washesInterval) * washesInterval;

  const revenueAxisMax =
    Math.ceil(maxRevenue / revenueInterval) * revenueInterval;

  const isMobile = window.innerWidth < 600;

  const formatWashesAxisValue = (value: number) => {
    if (isMobile) {
      if (value >= 1_000_000) return `${value / 1_000_000}M`;
      if (value >= 1_000) return `${value / 1_000}k`;
    }

    return value.toLocaleString();
  };

  const formatRevenueAxisValue = (value: number) => {
    if (isMobile) {
      if (value === 0) {
        return "$0";
      }

      if (value >= 1_000_000) {
        return `$${value / 1_000_000}M`;
      }

      if (value >= 1_000) {
        return `$${value / 1_000}k`;
      }

      return `$${value}`;
    }

    return formatCurrency(value);
  };
  return (
    <Box
      sx={{
        boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.25)",
        borderRadius: "10px",
        backgroundColor: theme.palette.background.default,
        padding: "10px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 16px",
        }}
      >
        <Typography
          sx={{
            fontSize: "16px",
            fontWeight: 500,
          }}
        >
          Washes Per Tech/Lead vs Revenue Per Tech/Lead
        </Typography>
      </Box>

      <BarChart
        dataset={chartData}
        height={isMobile ? 260 : 350}
        margin={{
          left: isMobile ? 35 : 80,
          right: isMobile ? 45 : 80,
          top: isMobile ? 80 : 50,
          bottom: isMobile ? 80 : 70,
        }}
        xAxis={[
          {
            scaleType: "band",
            dataKey: "tech",
            label: "",

            tickLabelStyle: {
              fontSize: isMobile ? 7 : 11,
              angle: isMobile ? -90 : -35,
              textAnchor: "end",
            },
          },
        ]}
        yAxis={[
          {
            id: "washesAxis",
            label: isMobile ? "" : "Washes Per Tech/Lead",
            min: 0,
            max: washesAxisMax,
            tickNumber: 6,
            valueFormatter: formatWashesAxisValue,
          },
          {
            id: "revenueAxis",
            label: isMobile ? "" : "Revenue Per Tech/Lead ",
            min: 0,
            max: revenueAxisMax,
            position: "right",
            tickNumber: 6,
            valueFormatter: formatRevenueAxisValue,
          },
        ]}
        series={[
          {
            dataKey: "washes",
            label: isMobile ? "Washes" : "Washes Per Tech/Lead",
            color: "#10B7B1",
            yAxisId: "washesAxis",
            valueFormatter: (value: number | null) =>
              value != null ? Math.round(value).toLocaleString() : "",
          },
          {
            dataKey: "revenue",
            label: isMobile ? "Revenue" : "Revenue Per Tech/Lead",
            color: "#3f8da9",
            yAxisId: "revenueAxis",
            valueFormatter: (value: number | null) =>
              value != null ? formatCurrency(value) : "",
          },
        ]}
        leftAxis="washesAxis"
        rightAxis="revenueAxis"
        slotProps={{
          bar: {
            rx: 4,
            ry: 4,
            style: {
              transform: "scaleX(0.6)",
              transformBox: "fill-box",
              transformOrigin: "center",
            },
          },
        }}
        sx={{
          padding: isMobile ? "5px" : "20px",

          [`.${axisClasses.left} .${axisClasses.label}`]: {
            transform: isMobile ? "translate(0px, 0)" : "translate(-70px, 0)",
          },

          [`.${axisClasses.right} .${axisClasses.label}`]: {
            transform: isMobile ? "translate(0px, 0)" : "translate(80px, 0)",
          },
          [`& .MuiChartsLegend-root`]: {
            fontSize: isMobile ? 10 : 14,
          },
        }}
      />
    </Box>
  );
};
